import { useEffect, useRef, useState } from 'react';

// ── GradientText ────────────────────────────────────────────────────────────
// reactbits "gradient-text": animated gradient sweeping through the text.
export function GradientText({ text, colors = ['#0D9488', '#22d3ee', '#0D9488'], style }) {
  return (
    <span style={{
      backgroundImage: `linear-gradient(90deg, ${colors.join(',')})`,
      backgroundSize: '250% auto', WebkitBackgroundClip: 'text', backgroundClip: 'text',
      WebkitTextFillColor: 'transparent', color: 'transparent',
      animation: 'fx-gradient 5s linear infinite', ...style,
    }}>{text}</span>
  );
}

// ── ProximityText ───────────────────────────────────────────────────────────
// reactbits "variable-proximity" + "text-pressure": each letter scales / gains
// weight as the cursor nears it. Works with any font (uses transform scale).
export function ProximityText({ text = '', radius = 130, style, letterStyle }) {
  const ref = useRef(null);
  const letters = useRef([]);
  useEffect(() => {
    const onMove = (e) => {
      letters.current.forEach((el) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const d = Math.hypot(e.clientX - cx, e.clientY - cy);
        const t = Math.max(0, 1 - d / radius); // 0..1
        el.style.transform = `scale(${1 + t * 0.35}) translateY(${-t * 4}px)`;
        el.style.fontWeight = String(Math.round(400 + t * 500));
      });
    };
    const reset = () => letters.current.forEach((el) => { if (el) { el.style.transform = 'none'; el.style.fontWeight = ''; } });
    window.addEventListener('pointermove', onMove);
    const node = ref.current;
    node?.addEventListener('pointerleave', reset);
    return () => { window.removeEventListener('pointermove', onMove); node?.removeEventListener('pointerleave', reset); };
  }, [radius, text]);
  return (
    <span ref={ref} style={{ display: 'inline-block', ...style }}>
      {String(text).split('').map((ch, i) => (
        <span key={i} ref={(el) => (letters.current[i] = el)}
          style={{ display: 'inline-block', transition: 'transform 0.18s ease, font-weight 0.18s ease', whiteSpace: 'pre', ...letterStyle }}>
          {ch}
        </span>
      ))}
    </span>
  );
}

// ── RotatingText ────────────────────────────────────────────────────────────
// reactbits "rotating-text": cycles through words with a slide/fade.
export function RotatingText({ items = [], interval = 2200, style, accent }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);
  return (
    <span style={{ display: 'inline-block', position: 'relative', overflow: 'hidden', verticalAlign: 'bottom', color: accent, ...style }}>
      <span key={i} style={{ display: 'inline-block', animation: 'fx-rotate-in 0.5s cubic-bezier(.2,.7,.2,1)' }}>{items[i]}</span>
    </span>
  );
}

// ── ScrollFloat ─────────────────────────────────────────────────────────────
// reactbits "scroll-float": element rises + fades + settles as it enters view.
export function ScrollFloat({ children, as: Tag = 'div', style, delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} style={{
      transform: shown ? 'translateY(0) scale(1)' : 'translateY(36px) scale(0.98)',
      opacity: shown ? 1 : 0, filter: shown ? 'blur(0)' : 'blur(4px)',
      transition: `transform 0.7s cubic-bezier(.2,.7,.2,1) ${delay}s, opacity 0.7s ease ${delay}s, filter 0.7s ease ${delay}s`,
      ...style,
    }}>{children}</Tag>
  );
}

// ── StarBorder ──────────────────────────────────────────────────────────────
// reactbits "star-border": an animated glinting gradient BORDER only — the
// interior stays transparent (no fill). Uses a masked ::before ring so the
// conic gradient shows purely along the edge. Pass the CTA button as children;
// give that button `background: transparent` so the animated border reads.
export function StarBorder({ children, color = '#0D9488', radius = 999, thickness = 2, style }) {
  return (
    <span className="fx-star-border" style={{ display: 'inline-block', borderRadius: radius, padding: thickness, '--fx-c': color, ...style }}>
      {children}
    </span>
  );
}
